import {
  BUILDING_TYPES,
  type ContactDto,
  contactInputSchema,
  type DuplicateCandidateDto,
  formatPhone,
  normalizeNip,
  type PartyDto,
  partyInputSchema,
  SITE_ROLES,
  type SiteDto,
  siteInputSchema,
  type SiteRole,
} from '@bqcrm/shared';
import { Alert, Badge, Button, Checkbox, Grid, Group, NumberInput, Paper, SegmentedControl, Select, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconPlus, IconUserCheck } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, HttpError } from '../../api';
import { usePostalCity } from '../../postalCity';
import { formatPostalCode } from '../../format';
import { notifyOk } from '../../notify';
import { fillFromRegistry, NipInput, NipLookupInfo, useNipLookup } from '../../components/NipLookup';

const issues = (r: { success: false; error: { issues: { message: string }[] } }) => [...new Set(r.error.issues.map((i) => i.message))];

/** Formularz klienta; przy tworzeniu serwer blokuje pewne duplikaty (409) — użytkownik decyduje. */
export function PartyForm({ party, onDone, onCancel }: { party: PartyDto | null; onDone: (p: PartyDto) => void; onCancel?: () => void }) {
  const [f, setF] = useState({
    kind: party?.kind ?? ('osoba' as 'osoba' | 'firma'),
    firstName: party?.firstName ?? '',
    lastName: party?.lastName ?? '',
    name: party?.name ?? '',
    nip: party?.nip ?? '',
    street: party?.street ?? '',
    postalCode: party?.postalCode ?? '',
    city: party?.city ?? '',
    phones: party?.phones.length ? [...party.phones] : [''],
    emails: party?.emails.length ? [...party.emails] : [''],
    consentReminders: party?.consentReminders ?? true,
    consentMarketing: party?.consentMarketing ?? false,
    notes: party?.notes ?? '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [dups, setDups] = useState<DuplicateCandidateDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const cityHint = usePostalCity(f.postalCode, f.city, (v) => set('city', v));
  // rejestry pytane dopiero po zmianie NIP (otwarcie edycji nie wysyła zapytań)
  const nipQ = useNipLookup(f.nip, f.kind === 'firma' && normalizeNip(f.nip) !== (party?.nip ?? ''));
  useEffect(() => {
    if (nipQ.data && nipQ.data.nip === normalizeNip(f.nip)) setF((x) => fillFromRegistry(x, nipQ.data));
  }, [nipQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // podpowiedź na żywo przy dodawaniu: „Czy to ktoś z bazy?” (telefon, e-mail, NIP, nazwa + adres) — nie blokuje zapisu
  const displayName = f.kind === 'osoba' ? `${f.firstName} ${f.lastName}`.trim() : f.name.trim();
  const [hintKey] = useDebouncedValue(
    JSON.stringify({ phone: f.phones[0] ?? '', email: f.emails[0] ?? '', nip: f.kind === 'firma' ? f.nip : '', displayName: f.kind === 'firma' ? displayName : '', lastName: f.kind === 'osoba' ? f.lastName.trim() : '', street: f.street, city: f.city }),
    400,
  );
  const hintQ = useQuery({
    queryKey: ['party-form-dups', hintKey],
    queryFn: () => {
      const p = JSON.parse(hintKey) as Record<'phone' | 'email' | 'nip' | 'displayName' | 'lastName' | 'street' | 'city', string>;
      const qs = new URLSearchParams();
      if (p.phone.replace(/\D/g, '').length >= 9) qs.set('phone', p.phone);
      if (p.email.includes('@')) qs.set('email', p.email);
      if (p.nip.replace(/\D/g, '').length === 10) qs.set('nip', p.nip);
      if (p.lastName.length >= 2) qs.set('lastName', p.lastName);
      if (p.displayName.length >= 3) qs.set('name', p.displayName);
      if (p.street.trim()) qs.set('street', p.street);
      if (p.city.trim()) qs.set('city', p.city);
      return qs.size ? api<DuplicateCandidateDto[]>(`/parties/duplicates?${qs}`) : Promise.resolve([]);
    },
    enabled: !party && !dups,
  });
  const hints = party || dups ? [] : (hintQ.data ?? []);

  const save = async (force: boolean) => {
    const body = { ...f, phones: f.phones.map((p) => p.trim()).filter(Boolean), emails: f.emails.map((e) => e.trim()).filter(Boolean) };
    const r = partyInputSchema.safeParse(body);
    if (!r.success) return setErrors(issues(r));
    setErrors([]);
    setBusy(true);
    try {
      const saved = party
        ? await api<PartyDto>(`/parties/${party.id}`, { method: 'PUT', body: r.data })
        : await api<PartyDto>('/parties', { method: 'POST', body: { party: r.data, force } });
      notifyOk(party ? 'Zapisano dane klienta' : 'Dodano klienta');
      void qc.invalidateQueries({ queryKey: ['parties'] });
      void qc.invalidateQueries({ queryKey: ['party', saved.id] });
      onDone(saved);
    } catch (e) {
      if (e instanceof HttpError && e.status === 409 && (e.data as { candidates?: DuplicateCandidateDto[] })?.candidates) {
        setDups((e.data as { candidates: DuplicateCandidateDto[] }).candidates);
      } else setErrors([errorText(e)]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <SegmentedControl
        value={f.kind}
        onChange={(v) => set('kind', v as 'osoba' | 'firma')}
        data={[
          { value: 'osoba', label: 'Osoba prywatna' },
          { value: 'firma', label: 'Firma' },
        ]}
        w="fit-content"
      />
      {f.kind === 'osoba' ? (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Imię" value={f.firstName} onChange={(e) => set('firstName', e.currentTarget.value)} />
          <TextInput label="Nazwisko" required value={f.lastName} onChange={(e) => set('lastName', e.currentTarget.value)} />
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <NipInput value={f.nip} onChange={(v) => set('nip', v)} loading={nipQ.isFetching} />
          <TextInput label="Nazwa firmy" required value={f.name} onChange={(e) => set('name', e.currentTarget.value)} />
          <div style={{ gridColumn: '1 / -1' }}>
            <NipLookupInfo lookup={nipQ} onApply={(d) => setF((x) => fillFromRegistry(x, d, true))} />
          </div>
        </SimpleGrid>
      )}
      <Text size="sm" fw={600}>
        Adres korespondencyjny
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <TextInput label="Ulica i numer" value={f.street} onChange={(e) => set('street', e.currentTarget.value)} />
        <TextInput label="Kod pocztowy" placeholder="00-000" inputMode="numeric" maxLength={6} value={f.postalCode} onChange={(e) => set('postalCode', formatPostalCode(e.currentTarget.value))} />
        <TextInput label="Miejscowość" value={f.city} description={cityHint} onChange={(e) => set('city', e.currentTarget.value)} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Stack gap={4}>
          {f.phones.map((p, i) => (
            <TextInput key={i} label={i === 0 ? 'Telefon' : undefined} value={p} onChange={(e) => set('phones', f.phones.map((x, j) => (j === i ? e.currentTarget.value : x)))} />
          ))}
          {f.phones.length < 3 && (
            <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={() => set('phones', [...f.phones, ''])} w="fit-content">
              Dodaj telefon
            </Button>
          )}
        </Stack>
        <Stack gap={4}>
          {f.emails.map((p, i) => (
            <TextInput key={i} label={i === 0 ? 'E-mail' : undefined} value={p} onChange={(e) => set('emails', f.emails.map((x, j) => (j === i ? e.currentTarget.value : x)))} />
          ))}
          {f.emails.length < 3 && (
            <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={() => set('emails', [...f.emails, ''])} w="fit-content">
              Dodaj e-mail
            </Button>
          )}
        </Stack>
      </SimpleGrid>
      <Group>
        <Checkbox label="Zgoda na przypomnienia (przeglądy)" checked={f.consentReminders} onChange={(e) => set('consentReminders', e.currentTarget.checked)} />
        <Checkbox label="Zgoda marketingowa" checked={f.consentMarketing} onChange={(e) => set('consentMarketing', e.currentTarget.checked)} />
      </Group>
      <Textarea label="Notatki" autosize minRows={2} value={f.notes} onChange={(e) => set('notes', e.currentTarget.value)} />
      {hints.length > 0 && (
        <Alert color={hints.some((d) => d.strong) ? 'red' : 'yellow'} icon={<IconAlertTriangle />} title="Czy to ktoś z bazy?" p="xs">
          <Stack gap={6}>
            {hints.map((d) => (
              <Paper key={d.id} withBorder p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text size="sm" fw={600}>
                      {d.displayName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[d.sites[0]?.address || d.city, ...d.phones.map(formatPhone), ...d.emails].filter(Boolean).join(' · ')}
                    </Text>
                    <Group gap={4} mt={2}>
                      {d.reasons.map((r) => (
                        <Badge key={r} size="xs" color={d.strong ? 'red' : 'yellow'} variant="light">
                          {r}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                  <Button size="xs" variant="light" component={Link} to={`/klienci/${d.id}`} leftSection={<IconUserCheck size={14} />}>
                    To ten klient
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Alert>
      )}
      {dups && (
        <Alert color="red" icon={<IconAlertTriangle />} title="W bazie jest już klient z tymi danymi kontaktowymi">
          <Stack gap={6}>
            {dups.map((d) => (
              <Paper key={d.id} withBorder p="xs">
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={600} component={Link} to={`/klienci/${d.id}`}>
                      {d.displayName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[d.city, ...d.phones.map(formatPhone), ...d.emails].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                  <Group gap={4}>
                    {d.reasons.map((r) => (
                      <Badge key={r} size="xs" color="red" variant="light">
                        {r}
                      </Badge>
                    ))}
                  </Group>
                </Group>
              </Paper>
            ))}
            <Button size="xs" color="red" variant="outline" onClick={() => void save(true)} loading={busy} w="fit-content">
              To inny klient — zapisz mimo to
            </Button>
          </Stack>
        </Alert>
      )}
      {errors.length > 0 && (
        <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
          {errors.join('\n')}
        </Alert>
      )}
      <Group justify="flex-end">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Anuluj
          </Button>
        )}
        <Button onClick={() => void save(false)} loading={busy}>
          {party ? 'Zapisz' : 'Dodaj klienta'}
        </Button>
      </Group>
    </Stack>
  );
}

export function ContactForm({ partyId, contact, onDone, onCancel }: { partyId: string; contact: ContactDto | null; onDone: () => void; onCancel?: () => void }) {
  const [f, setF] = useState({ fullName: contact?.fullName ?? '', role: contact?.role ?? '', phone: contact?.phone ?? '', email: contact?.email ?? '', notes: contact?.notes ?? '' });
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = <K extends keyof typeof f>(k: K, v: string) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const r = contactInputSchema.safeParse(f);
    if (!r.success) return setErrors(issues(r));
    setBusy(true);
    try {
      if (contact) await api(`/contacts/${contact.id}`, { method: 'PUT', body: r.data });
      else await api(`/parties/${partyId}/contacts`, { method: 'POST', body: r.data });
      notifyOk(contact ? 'Zapisano osobę kontaktową' : 'Dodano osobę kontaktową');
      void qc.invalidateQueries({ queryKey: ['party', partyId] });
      onDone();
    } catch (e) {
      setErrors([errorText(e)]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack>
      <TextInput label="Imię i nazwisko" value={f.fullName} onChange={(e) => set('fullName', e.currentTarget.value)} data-autofocus />
      <TextInput label="Rola / relacja" placeholder="np. żona, kierownik budowy, zarządca" value={f.role} onChange={(e) => set('role', e.currentTarget.value)} />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput label="Telefon" value={f.phone} onChange={(e) => set('phone', e.currentTarget.value)} />
        <TextInput label="E-mail" value={f.email} onChange={(e) => set('email', e.currentTarget.value)} />
      </SimpleGrid>
      <TextInput label="Notatki" value={f.notes} onChange={(e) => set('notes', e.currentTarget.value)} />
      {errors.length > 0 && (
        <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
          {errors.join('\n')}
        </Alert>
      )}
      <Group justify="flex-end">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Anuluj
          </Button>
        )}
        <Button onClick={() => void save()} loading={busy}>
          {contact ? 'Zapisz' : 'Dodaj osobę'}
        </Button>
      </Group>
    </Stack>
  );
}

export function SiteForm({
  site,
  partyId,
  onDone,
  onCancel,
}: {
  site: SiteDto | null;
  /** przy tworzeniu: klient, z którym lokalizacja zostanie powiązana */
  partyId?: string;
  onDone: (s: SiteDto) => void;
  onCancel?: () => void;
}) {
  const [f, setF] = useState({
    street: site?.street ?? '',
    postalCode: site?.postalCode ?? '',
    city: site?.city ?? '',
    buildingType: site?.buildingType ?? 'dom_jednorodzinny',
    usableArea: site?.usableArea ?? ('' as number | ''),
    accessNotes: site?.accessNotes ?? '',
    technicalNotes: site?.technicalNotes ?? '',
  });
  const [role, setRole] = useState<SiteRole>('wlasciciel');
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const cityHint = usePostalCity(f.postalCode, f.city, (v) => set('city', v));
  const save = async () => {
    const r = siteInputSchema.safeParse({ ...f, usableArea: f.usableArea === '' ? null : Number(f.usableArea), lat: site?.lat ?? null, lng: site?.lng ?? null });
    if (!r.success) return setErrors(issues(r));
    setBusy(true);
    try {
      const saved = site
        ? await api<SiteDto>(`/sites/${site.id}`, { method: 'PUT', body: r.data })
        : await api<SiteDto>('/sites', { method: 'POST', body: { site: r.data, partyId, role } });
      notifyOk(site ? 'Zapisano lokalizację' : 'Dodano lokalizację');
      void qc.invalidateQueries({ queryKey: ['party'] });
      void qc.invalidateQueries({ queryKey: ['site', saved.id] });
      onDone(saved);
    } catch (e) {
      setErrors([errorText(e)]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack>
      {/* telefon: pola jedno pod drugim; szerzej: ulica na 2/3 wiersza */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <TextInput label="Ulica i numer" value={f.street} onChange={(e) => set('street', e.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput label="Kod pocztowy" placeholder="00-000" inputMode="numeric" maxLength={6} value={f.postalCode} onChange={(e) => set('postalCode', formatPostalCode(e.currentTarget.value))} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput label="Miejscowość" required value={f.city} description={cityHint} onChange={(e) => set('city', e.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Select
            label="Typ budynku"
            data={Object.entries(BUILDING_TYPES).map(([value, label]) => ({ value, label }))}
            value={f.buildingType}
            onChange={(v) => set('buildingType', v as typeof f.buildingType)}
            clearable
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <NumberInput label="Powierzchnia użytkowa (m²)" value={f.usableArea} onChange={(v) => set('usableArea', v === '' ? '' : Number(v))} min={1} hideControls />
        </Grid.Col>
      </Grid>
      <Textarea label="Dojazd, dostęp" autosize minRows={2} value={f.accessNotes} onChange={(e) => set('accessNotes', e.currentTarget.value)} />
      <Textarea label="Informacje techniczne (kotłownia, przyłącza, dach…)" autosize minRows={2} value={f.technicalNotes} onChange={(e) => set('technicalNotes', e.currentTarget.value)} />
      {!site && (
        <Select label="Rola klienta" data={Object.entries(SITE_ROLES).map(([value, label]) => ({ value, label }))} value={role} onChange={(v) => v && setRole(v as SiteRole)} allowDeselect={false} />
      )}
      {errors.length > 0 && (
        <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
          {errors.join('\n')}
        </Alert>
      )}
      <Group justify="flex-end">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Anuluj
          </Button>
        )}
        <Button onClick={() => void save()} loading={busy}>
          {site ? 'Zapisz' : 'Dodaj lokalizację'}
        </Button>
      </Group>
    </Stack>
  );
}
